<template>
  <nav>
    <v-card height="100%" elevation="0" :class="{'pa-4':editor}" class="design-card" data-testid="design-card" :key="editorExtension">
      <div
        v-if="editor && (editorExtension == 'text' || editorExtension == 'accordion-summary' || editorExtension == 'accordion-content')">
        <!-- just to load css for v-button-toggle -->
        <v-btn-toggle class="d-none" />
        <div class="d-flex flex-row v-item-group theme--light v-btn-toggle mb-4 rounded-lg elevation-2"
          v-if="editor.extensionStorage.hasOwnProperty('bold') || editor.extensionStorage.hasOwnProperty('italic') || editor.extensionStorage.hasOwnProperty('strike') || editor.extensionStorage.hasOwnProperty('underline')">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editor.extensionStorage.hasOwnProperty('bold')" small class="flex-grow-1"
                :class="{ 'v-btn--active': editor.isActive('bold') }" icon
                @click="editor.chain().focus().toggleBold().run()" v-on="on">
                <v-icon>mdi-format-bold</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorBold') }} (Ctrl+B)</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editor.extensionStorage.hasOwnProperty('italic')" small class="flex-grow-1"
                :class="{ 'v-btn--active': editor.isActive('italic') }" icon
                @click="editor.chain().focus().toggleItalic().run()" v-on="on">
                <v-icon>mdi-format-italic</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorItalic') }} (Ctrl+I)</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editor.extensionStorage.hasOwnProperty('strike')" small class="flex-grow-1"
                :class="{ 'v-btn--active': editor.isActive('strike') }" icon
                @click="editor.chain().focus().toggleStrike().run()" v-on="on">
                <v-icon>mdi-format-strikethrough-variant</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorStrikethrough') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editor.extensionStorage.hasOwnProperty('underline')" small class="flex-grow-1"
                :class="{ 'v-btn--active': editor.isActive('underline') }" icon
                @click="editor.chain().focus().toggleUnderline().run()" v-on="on">
                <v-icon>mdi-format-underline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorUnderline') }} (Ctrl+U)</span>
          </v-tooltip>
        </div>
        <div class="d-flex flex-row v-item-group theme--light v-btn-toggle mb-4 rounded-lg elevation-2"
          v-if="editorExtension != 'accordion-summary' && editor.extensionStorage.hasOwnProperty('textAlign')">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn small class="flex-grow-1" :class="{ 'v-btn--active': editor.isActive({ textAlign: 'left' }) }" icon
                @click="editor.chain().focus().setTextAlign('left').run()" v-on="on">
                <v-icon>mdi-format-align-left</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAlignLeft') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn small class="flex-grow-1" :class="{ 'v-btn--active': editor.isActive({ textAlign: 'center' }) }" icon
                @click="editor.chain().focus().setTextAlign('center').run()" v-on="on">
                <v-icon>mdi-format-align-center</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAlignCenter') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn small class="flex-grow-1" :class="{ 'v-btn--active': editor.isActive({ textAlign: 'right' }) }" icon
                @click="editor.chain().focus().setTextAlign('right').run()" v-on="on">
                <v-icon>mdi-format-align-right</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAlignRight') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn small class="flex-grow-1" :class="{ 'v-btn--active': editor.isActive({ textAlign: 'justify' }) }"
                icon @click="editor.chain().focus().setTextAlign('justify').run()" v-on="on">
                <v-icon>mdi-format-align-justify</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAlignJustify') }}</span>
          </v-tooltip>
        </div>
        <div v-if="editorExtension != 'accordion-summary' && editor.extensionStorage.hasOwnProperty('heading')">
          <v-select v-model="selectedTextSize" solo class="rounded-xxl select-items elevation-4" hide-details="true"
            append-icon="mdi-chevron-down" dense :items="textSizes" item-text="name" return-object
            :menu-props="{ maxHeight: 350 }" @input="setTextSize">
            <template v-slot:item="{ item }">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="item.html" />
            </template>
          </v-select>
        </div>
        <div :class="editor.extensionStorage.hasOwnProperty('heading') ? 'my-5' : 'mb-5'" data-testid="text-color">
          <span class="text-subtitle-1">{{ $t('recentColorsTitle') }}</span>
          <div class="text-center">
            <v-btn v-for="index in 6" :key="index" class="ma-1 pa-0 elevation-2" height="32" width="32" min-width="32"
              :color="recentColors[index - 1] ? recentColors[index - 1] : '#FFFFFF'"
              @click="editor.chain().setColor(recentColors[index - 1]).run()" :class="{ 'disable-events': !recentColors[index - 1] }">
              <v-icon
                v-if="recentColors[index - 1] && (rgb2hex(editor.getAttributes('textStyle').color) == recentColors[index - 1] || rgb2hex(editor.getAttributes('textStyle').color) == recentColors[index - 1].toUpperCase())"
                color="greytext">
                mdi-check
              </v-icon>
            </v-btn>
          </div>
          <span class="text-subtitle-1">{{ $t('defaultColorsTitle') }}</span>
          <div class="text-center">
            <v-btn v-for="(color, index) in colorPalette" :key="index" class="ma-1 pa-0 elevation-2" height="32"
              width="32" min-width="32" :color="color" @click="editor.chain().setColor(color).run()">
              <v-icon
                v-if="rgb2hex(editor.getAttributes('textStyle').color) == color || rgb2hex(editor.getAttributes('textStyle').color) == color.toUpperCase()"
                color="greytext">
                mdi-check
              </v-icon>
            </v-btn>
          </div>
          <v-card class="mx-auto rounded-xxl mt-2 elevation-4" width="100%">
            <v-card-actions class="pa-0">
              <v-btn :ripple="false" elevation="0" :class="showAdvancedColors ? 'rounded-tl-xl' : 'rounded-xxl'"
                class="text-none no-hover" width="85%" color="white" @click="showAdvancedColors = !showAdvancedColors">
                {{ $t('advancedColors') }}
              </v-btn>
              <v-spacer />
              <v-btn icon @click="showAdvancedColors = !showAdvancedColors" :ripple="false" class="no-hover">
                <v-icon>{{ showAdvancedColors ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </v-card-actions>
            <v-expand-transition>
              <div v-show="showAdvancedColors" class="ma-2 text-center">
                <v-divider />
                <v-color-picker class="rounded-bl-xl rounded-br-xl" :value="colorPickerTextValue"
                  @input="setColorPickerColor" data-testid="color-picker"/>
                <v-btn color="accent" elevation="0" class="rounded-xxl text-none px-4 mb-2 white--text" small
                  @click="setTextColor(colorPickerValue)" data-testid="set-color-button">
                  {{ $t('applyColor') }}
                </v-btn>
              </div>
            </v-expand-transition>
          </v-card>
        </div>
        <div class="d-flex flex-row v-item-group theme--light v-btn-toggle mb-4 rounded-lg elevation-2"
          v-if="(editor.extensionStorage.hasOwnProperty('bulletList') && editorExtension != 'accordion-summary') || (editor.extensionStorage.hasOwnProperty('orderedList') && editorExtension != 'accordion-summary') || (editor.extensionStorage.hasOwnProperty('highlight')) || (editor.extensionStorage.hasOwnProperty('custom-link'))">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editor.extensionStorage.hasOwnProperty('bulletList') && editorExtension != 'accordion-summary'"
                small class="flex-grow-1" :class="{ 'v-btn--active': editor.isActive('bulletList') }" icon
                @click="editor.chain().focus().toggleBulletList().run()" v-on="on">
                <v-icon>mdi-format-list-bulleted</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAddBulletedList') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                v-if="editor.extensionStorage.hasOwnProperty('orderedList') && editorExtension != 'accordion-summary'"
                small class="flex-grow-1" :class="{ 'v-btn--active': editor.isActive('orderedList') }" icon
                @click="editor.chain().focus().toggleOrderedList().run()" v-on="on">
                <v-icon>mdi-format-list-numbered</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAddNumberedList') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editor.extensionStorage.hasOwnProperty('highlight')" small class="flex-grow-1"
                :class="{ 'v-btn--active': editor.isActive('highlight') }" icon
                @click="editor.chain().focus().toggleHighlight().run()" v-on="on">
                <v-icon>mdi-format-color-highlight</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorHighlightText') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editor.extensionStorage.hasOwnProperty('custom-link')" small class="flex-grow-1"
                data-testid="link-button" :class="{ 'v-btn--active': editor.isActive('custom-link') }" icon
                @click="dialogVisibleURL = true" v-on="on">
                <v-icon>mdi-link</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAddCustomLink') }}</span>
          </v-tooltip>
          <v-tooltip bottom
            v-if="editor.isActive('custom-link') && editor.extensionStorage.hasOwnProperty('custom-link')">
            <template v-slot:activator="{ on }">
              <v-btn small class="flex-grow-1" :class="{ 'v-btn--active': editor.isActive('custom-link') }" icon
                @click="removeLink" v-on="on">
                <v-icon>mdi-link-off</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorRemoveCustomLink') }}</span>
          </v-tooltip>
        </div>

        <v-card-actions class="py-0 px-0" ref="textActions">
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="page == 'activity_page' && editorExtension != 'accordion-summary'" small
                data-testid="table-button" icon
                @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()" v-on="on">
                <v-icon>mdi-table-large</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('addTable') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-if="editorExtension != 'accordion-summary' && editor.extensionStorage.hasOwnProperty('emoji')"
                small class="ml-1" data-testid="emoji-button" icon @click="toggleEmojiPicker" v-on="on">
                <v-icon>mdi-emoticon-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editorAddEmoji') }}</span>
          </v-tooltip>

        </v-card-actions>

        <v-divider class="my-5"
          v-if="(page == 'activity_page' && editor.isActive('table')) || (editor.isActive('custom-link')) || (showEmojiPicker && editorExtension != 'accordion-summary')"></v-divider>
        <div v-if="showEmojiPicker && editorExtension != 'accordion-summary'" class="mb-4">
          <CustomEmojiPicker ref="emojiList" class="elevation-2" :i18n="emojiTranslation"
            @changeCategory="selectEmojiCategory" @select="selectEmoji" :continuousList="true" emojiSize="24"
            emojisByRow="6" />
        </div>
        <div v-if="page == 'activity_page' && editor.isActive('table')" class="d-block rounded-xxl elevation-2">
          <v-list>
            <v-list-item data-testid="table-add-column-before" block class="rounded-xxl text-none"
              @click="editor.chain().focus().addColumnBefore().run()">
              <v-icon left>
                mdi-table-column-plus-before
              </v-icon>
              {{ $t('addColumnBefore') }}
            </v-list-item>
            <v-divider />
            <v-list-item data-testid="table-add-column-after" block class="rounded-xxl text-none text-left"
              @click="editor.chain().focus().addColumnAfter().run()">
              <v-icon left>
                mdi-table-column-plus-after
              </v-icon>
              {{ $t('addColumnAfter') }}
            </v-list-item>
            <v-divider />
            <v-list-item data-testid="table-add-row-before" block class="rounded-xxl text-none"
              @click="editor.chain().focus().addRowBefore().run()">
              <v-icon left>
                mdi-table-row-plus-before
              </v-icon>
              {{ $t('addRowBefore') }}
            </v-list-item>
            <v-divider />
            <v-list-item data-testid="table-add-row-after" block class="rounded-xxl text-none"
              @click="editor.chain().focus().addRowAfter().run()">
              <v-icon left>
                mdi-table-row-plus-after
              </v-icon>
              {{ $t('addRowAfter') }}
            </v-list-item>
            <v-divider />
            <v-list-item data-testid="table-delete-column" block class="rounded-xxl text-none"
              @click="editor.chain().focus().deleteColumn().run()">
              <v-icon left>
                mdi-delete
              </v-icon>
              {{ $t('deleteColumn') }}
            </v-list-item>
            <v-divider />
            <v-list-item data-testid="table-delete-row" block class="rounded-xxl text-none"
              @click="editor.chain().focus().deleteRow().run()">
              <v-icon left>
                mdi-delete
              </v-icon>
              {{ $t('deleteRow') }}
            </v-list-item>
            <v-divider />
            <v-list-item data-testid="table-delete" block class="rounded-xxl text-none"
              @click="editor.chain().focus().deleteTable().run()">
              <v-icon left>
                mdi-delete
              </v-icon>
              {{ $t('deleteTable') }}
            </v-list-item>
          </v-list>
        </div>
        <div v-if="editor.isActive('custom-link')">
          <v-checkbox v-model="checkboxDesignAsButton" :label="$t('designAsButton')" class="ma-0 px-2"
            data-testid="design-checkbox" @change="designAsButton" hide-details />
        </div>
        <v-card v-if="editor.isActive('custom-link') && checkboxDesignAsButton" elevation="0"
          data-testid="design-link-card">
          <div class="my-5">
            <span class="text-subtitle-1">{{ $t('recentColorsTitle') }}</span>
            <div class="text-center">
              <v-btn v-for="index in 6" :key="index" class="ma-1 pa-0 elevation-2" height="32" width="32" min-width="32"
                :color="recentColors[index - 1] ? recentColors[index - 1] : '#FFFFFF'"
                @click="editor.chain().extendMarkRange('custom-link').setLink({ backgroundColor: recentColors[index - 1] }).run()" :class="{ 'disable-events': !recentColors[index - 1] }">
                <v-icon
                  v-if="recentColors[index - 1] && (rgb2hex(editor.getAttributes('custom-link').backgroundColor) == recentColors[index - 1] || rgb2hex(editor.getAttributes('custom-link').backgroundColor) == recentColors[index - 1].toUpperCase())"
                  color="greytext">
                  mdi-check
                </v-icon>
              </v-btn>
            </div>
            <span class="text-subtitle-1">{{ $t('defaultColorsTitle') }}</span>
            <div class="text-center">
              <v-btn v-for="(color, index) in colorPalette" :key="index" class="ma-1 pa-0 elevation-2" height="32"
                width="32" min-width="32" :color="color"
                @click="editor.chain().extendMarkRange('custom-link').setLink({ backgroundColor: color }).run()">
                <v-icon
                  v-if="rgb2hex(editor.getAttributes('custom-link').backgroundColor) == color || rgb2hex(editor.getAttributes('custom-link').backgroundColor) == color.toUpperCase()"
                  color="greytext">
                  mdi-check
                </v-icon>
              </v-btn>
            </div>
            <v-card class="mx-auto rounded-xxl mt-2 elevation-4" width="100%">
              <v-card-actions class="pa-0">
                <v-btn :ripple="false" elevation="0" class="text-none no-hover"
                  :class="showAdvancedColorsButton ? 'rounded-tl-xl' : 'rounded-xxl'" width="85%" color="white"
                  @click="showAdvancedColorsButton = !showAdvancedColorsButton">
                  {{ $t('advancedColors') }}
                </v-btn>
                <v-spacer />
                <v-btn icon @click="showAdvancedColorsButton = !showAdvancedColorsButton" :ripple="false"
                  class="no-hover">
                  <v-icon>{{ showAdvancedColorsButton ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </v-card-actions>
              <v-expand-transition>
                <div v-show="showAdvancedColorsButton" class="ma-2 text-center">
                  <v-divider />
                  <v-color-picker class="ma-2 rounded-bl-xl rounded-br-xl" :value="colorPickerButtonValue"
                    @input="setColorPickerColor" />
                  <v-btn color="accent" elevation="0" class="rounded-xxl text-none px-4 mb-2 white--text" small
                    @click="setButtonColor(colorPickerValue)">
                    {{ $t('applyColor') }}
                  </v-btn>
                </div>
              </v-expand-transition>
            </v-card>
          </div>
          <div>
            <v-card class="mx-auto" width="100%" elevation="0">
              <v-select v-model="selectedButtonSize" solo class="rounded-xxl select-items" hide-details="true"
                append-icon="mdi-chevron-down" dense :items="buttonSizes" item-text="name" return-object
                :menu-props="{ maxHeight: 350 }" @input="setButtonSize">
                <template v-slot:item="{ item }">
                  <span> {{ item.name }}</span>
                </template>
              </v-select>
              <v-checkbox v-model="checkboxButtonRoundedCorners" :label="$t('roundedCornersButton')" class="px-2"
                @change="setButtonRoundedCorners" hide-details />
            </v-card>
          </div>
        </v-card>
        <ConfirmationDialogLink v-if="dialogVisibleURL && editorActivateLinkOption" ref="ConfirmationDialogLink"
          :visible="dialogVisibleURL" :url="editor.getAttributes('custom-link').href"
          data-testid="confirmation-dialog-link" @close="dialogVisibleURL = false" @proceedSubmit="setLinkHref" />
        <v-divider class="my-5"
          v-if="(page == 'activity_page' && editor.isActive('table')) || (editor.isActive('custom-link')) || (showEmojiPicker && editorExtension != 'accordion-summary')"></v-divider>
        <div v-if="page == 'activity_page' && !editor.isActive('table')" class="my-5 d-block">
          <v-btn data-testid="accordion-add-button" block class="rounded-xxl text-none accordion-btn elevation-2"
            color="white" :disabled="editor.isActive('details')" @click="addAccordion()">
            {{ $t('addAccordionBtn') }}
            <v-icon right>
              mdi-plus
            </v-icon>
          </v-btn>
        </div>
      </div>
      <div v-if="editor && (editorExtension == 'custom-image' || editorExtension == 'custom-image-banner')">
        <v-card-actions class="mb-2 pa-0" v-if="isGifUrl == false">
          <v-spacer />
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon data-testid="crop-button" :disabled="loadingUpload" class="px-0"
                @click="dialogVisibleCropper = true, cropOnUpload = false" v-on="on">
                <v-icon data-testid="crop-icon">
                  mdi-crop
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('cropImage') }}</span>
          </v-tooltip>
        </v-card-actions>
        <img style="max-width: 100%; max-height: 200px; width: auto" class="mx-auto d-block"
          :src="editorExtensionProperties.attrs.src">
        <v-card-actions class="py-4 px-0">
          <v-btn data-testid="upload-button" :loading="loadingUpload" :disabled="loadingUpload" color="white"
            class="text-body-1 darktext--text text-none rounded-xxl px-4" @click="showUploadImage">
            {{ $t('uploadNew') }}
          </v-btn>
          <v-file-input ref="file_input" v-model="imageData" accept="image/png, image/jpeg, image/gif" prepend-icon="mdi-camera"
            data-testid="file-input" class="d-none" @change="uploadImage" />
        </v-card-actions>
        <v-card-actions class="py-2 px-0 d-block" v-if="page != 'survey'">
          <v-row no-gutters>
            <v-col cols="12">
              <v-subheader>
                {{ $t('roundedCornersImage') }}
              </v-subheader>
            </v-col>
            <v-col cols="12">
              <v-slider v-model="imageRoundedCorners" data-testid="rounded-corners-slider" class="align-center px-4"
                max="50" min="0" hide-details @input="changeImageCorners">
                <template v-slot:append>
                  <v-text-field v-model="imageRoundedCorners" data-testid="rounded-corners-input"
                    class="mt-0 pt-0 image-size" hide-details single-line style="width: 50px" type="number"
                    hide-spin-buttons height="35px" @keypress="preventLetters" />
                </template>
              </v-slider>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-subheader>
                {{ $t('sizeImage') }}
              </v-subheader>
            </v-col>
            <v-col cols="12">
              <v-radio-group v-model="imageSize" row class="px-4 mt-0 mx-auto" data-testid="image-size-radio"
                style="width: fit-content" @change="changeImageSize">
                <v-radio value="small" class="d-flex flex-column" color="accent" data-testid="image-small-radio">
                  <template v-slot:label>
                    <span class="text-caption greytext--text mt-2">{{ $t('small') }}</span>
                  </template>
                </v-radio>
                <v-radio value="medium" class="d-flex flex-column" color="accent" data-testid="image-medium-radio">
                  <template v-slot:label>
                    <span class="text-caption greytext--text mt-2">{{ $t('medium') }}</span>
                  </template>
                </v-radio>
                <v-radio value="large" class="d-flex flex-column" color="accent" data-testid="image-large-radio">
                  <template v-slot:label>
                    <span class="text-caption greytext--text mt-2">{{ $t('large') }}</span>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card-actions>
        <ConfirmationDialogCropper v-if="dialogVisibleCropper && typeImge != 'image/gif'" ref="confirmationDialog" :visible="dialogVisibleCropper"
          :image-to-crop="imageToCropOnUpload ? imageToCropOnUpload : editorExtensionProperties.attrs.src"
          :image-type="imageTypeOnUpload" :crop-on-upload="cropOnUpload" data-testid="confirmation-dialog"
          @close="closeCropImage" @crop="cropImage" />
      </div>
      <div v-if="editor && editorExtension == 'iframe'">
        <div v-if="editorExtensionProperties.attrs.tag == 'external-video'">
          <div class="video-wrapper">
            <iframe :src="editorExtensionProperties.attrs.src" frameborder="0" allowfullscreen />
          </div>
          <v-card-actions class="py-4 px-0">
            <v-btn :loading="loadingEmbed" :disabled="loadingEmbed" data-testid="embed-button" color="white"
              class="text-body-1 darktext--text text-none rounded-xxl px-4" @click="showEmbedVideo">
              {{ $t('addNew') }}
            </v-btn>
          </v-card-actions>
          <ConfirmationDialogVideoEmbed v-if="dialogVisibleVideoEmbed" ref="confirmationDialogEmbed"
            :visible="dialogVisibleVideoEmbed" data-testid="confirmation-dialog" @close="dialogVisibleVideoEmbed = false"
            @proceedSubmit="embedVideo" />
        </div>
        <div v-else>
          <div>
            <img style="max-width: 100%; max-height: 200px; width: auto;" class="mx-auto d-block"
              src="../assets/editor_images/iframe_grey.jpg">
            <span v-if="editorExtensionProperties.attrs.src" data-testid="iframe-src"
              class="text-caption accent--text font-weight-regular d-block">{{ editorExtensionProperties.attrs.src
              }}</span>
          </div>
          <v-card-actions class="py-4 px-0">
            <v-btn :loading="loadingEmbed" :disabled="loadingEmbed" data-testid="embed-code-button" color="white"
              class="text-body-1 darktext--text text-none rounded-xxl px-4" @click="dialogVisibleCodeEmbed = true">
              {{ $t('addNewIframe') }}
            </v-btn>
            <ConfirmationDialogCodeEmbed v-if="dialogVisibleCodeEmbed" ref="confirmationDialogEmbed"
              :visible="dialogVisibleCodeEmbed" :code="editorExtensionProperties.attrs.src == '' ? '' : editor.getHTML()"
              data-testid="confirmation-dialog" @close="dialogVisibleCodeEmbed = false" @proceedSubmit="embedCode" />
          </v-card-actions>
        </div>
      </div>
      <div v-if="editor && editorExtension == 'document'">
        <v-btn data-testid="upload-button" :loading="loadingUpload" :disabled="loadingUpload" color="white" width="100%"
          class="text-body-1 darktext--text text-none rounded-xxl my-10" @click="showUploadDocument()">
          {{ editor.getHTML() && editor.getHTML().includes('mdi-download') ? $t('uploadNew') : $t('upload') }}
        </v-btn>
        <v-file-input ref="document_input" v-model="documentData"
          accept="image/png, image/jpeg, .pdf, .ppt, .pptx, .doc, .docx, .xls, .xlsx, .odt, .csv, .ods, .rtf, .txt"
          data-testid="document-input" class="d-none" @change="uploadDocument" />
      </div>
      <div v-if="editor && editorExtension == 'audio'">
        <img style="width: 100%" class="mx-auto d-block px-15" src="../assets/editor_images/audio.png">
        <span v-if="editorExtensionProperties.attrs.file" data-testid="audio-title"
          class="text-caption greytext--text font-weight-regular d-block px-15 text-truncate">{{
            editorExtensionProperties.attrs.title }}</span>
        <span v-else data-testid="audio-title" class="text-caption greytext--text font-weight-regular d-block px-15">{{
          $t('noAudioText') }}</span>
        <v-btn data-testid="upload-button" :loading="loadingUpload" :disabled="loadingUpload" color="white" width="100%"
          class="text-body-1 darktext--text text-none rounded-xxl mt-5" @click="showUploadAudio()">
          {{ editor.getHTML() && editor.getHTML().includes('mdi-download') ? $t('uploadNew') : $t('upload') }}
        </v-btn>
        <v-file-input ref="audio_input" v-model="audioData" accept=".mp3, .wav, .ogg" data-testid="audio-input"
          class="d-none" @change="uploadAudio" />
      </div>
    </v-card>
  </nav>
</template>
<script>
import { Editor } from '@tiptap/vue-2'
import i18n from '../plugins/i18n'
import { uploadImageInActivityPage, uploadDocumentInActivityPage, uploadImageInChecklist, uploadAudioInActivityPage, uploadImageInSurvey } from '../api/activityApi'
import ConfirmationDialogCropper from '../components/ConfirmationDialogCropper';
import ConfirmationDialogVideoEmbed from '../components/ConfirmationDialogVideoEmbed';
import ConfirmationDialogLink from '../components/ConfirmationDialogLink';
import { VEmojiPicker } from 'v-emoji-picker';
import ConfirmationDialogCodeEmbed from '../components/ConfirmationDialogCodeEmbed';

const CustomEmojiPicker = VEmojiPicker.extend({
  created() {
    //fixed bug - Continious list categories not translated when supplying i18n
    this.$nextTick(() => {
      var all = document.getElementsByClassName('category-title')
      for (let i = 0; i < all.length; i++) {
        all[i].innerText = i18n.t('emojiCategory' + all[i].innerHTML)
      }
    })
  }
})
export default {
  components: {
    ConfirmationDialogCropper,
    ConfirmationDialogVideoEmbed,
    ConfirmationDialogLink,
    CustomEmojiPicker,
    ConfirmationDialogCodeEmbed
  },
  props: {
    editor: Editor,
    editorExtension: {
      type: String,
      default: ''
    },
    editorExtensionProperties: {
      type: Object,
      default: () => { }
    },
    editorTextSize: {
      type: String,
      default: ''
    },
    editorActivateLinkOption: Boolean,
    editorLinkProperties: {
      type: Object,
      default: () => { }
    },
    colors: {
      type: Array,
      default: () => []
    },
    page: {
      type: String,
      default: 'activity_page'
    },
  },
  data() {
    return {
      textSizes: [
        { code: 'p-normal', name: 'Normal text', html: '<p class="mb-0">Normal text</p>' },
        { code: 'p-small', name: 'Small text', html: '<p class="small mb-0">Small text</p>' },
        { code: 'h1', name: 'Heading 1', html: '<h1>Heading 1</h1>', level: 1 },
        { code: 'h2', name: 'Heading 2', html: '<h2>Heading 2</h2>', level: 2 },
        { code: 'h3', name: 'Heading 3', html: '<h3>Heading 3</h3>', level: 3 },
        { code: 'h4', name: 'Heading 4', html: '<h4>Heading 4</h4>', level: 4 },
        { code: 'h5', name: 'Heading 5', html: '<h5>Heading 5</h5>', level: 5 },
        { code: 'h6', name: 'Heading 6', html: '<h6>Heading 6</h6>', level: 6 },
      ],
      selectedTextSize: { code: 'p-normal', name: 'Normal text', html: '<p>Normal text</p>' },
      showAdvancedColors: false,
      showAdvancedColorsButton: false,
      colorPalette: ["#000000", "#545454", "#737373", "#A6A6A6", "#D9D9D9", "#FFFFFF"],
      selection: '',
      imageData: null,
      spaceId: window.$cookies.get('space_id'),
      activityId: this.$route.params.id,
      pageId: this.$route.params.pageId,
      checklistId: this.$route.params.checklistId,
      surveyId: this.$route.params.surveyId,
      loadingUpload: false,
      loadingEmbed: false,
      dialogVisibleCropper: false,
      dialogVisibleVideoEmbed: false,
      imageToCropOnUpload: null,
      imageTypeOnUpload: '',
      cropOnUpload: false,
      showEmbedDialog: false,
      dialogVisibleURL: false,
      buttonSizes: [
        { size: 12, name: i18n.t('small') },
        { size: 16, name: i18n.t('medium') },
        { size: 20, name: i18n.t('large') },
      ],
      selectedButtonSize: { size: 12, name: i18n.t('small') },
      checkboxButtonRoundedCorners: 0,
      checkboxDesignAsButton: false,
      documentData: null,
      documentIcons: [
        { type: 'pdf', color: '#ff1744', icon: 'mdi-file-pdf-box' },
        { type: 'ppt', color: '#ff1744', icon: 'mdi-file-powerpoint-outline' },
        { type: 'pptx', color: '#ff1744', icon: 'mdi-file-powerpoint-outline' },
        { type: 'doc', color: '#2979FF', icon: 'mdi-file-document-outline' },
        { type: 'docx', color: '#2979FF', icon: 'mdi-file-document-outline' },
        { type: 'xls', color: '#00C853', icon: 'mdi-file-excel-outline' },
        { type: 'xlsx', color: '#00C853', icon: 'mdi-file-excel-outline' },
        { type: 'odt', color: '#2979FF', icon: 'mdi-file-document-outline' },
        { type: 'csv', color: '#00C853', icon: 'mdi-file-delimited-outline' },
        { type: 'ods', color: '#00C853', icon: 'mdi-file-excel-outline' },
        { type: 'rtf', color: '#2979FF', icon: 'mdi-file-document-outline' },
        { type: 'txt', color: '#2979FF', icon: 'mdi-file-document-outline' },
        { type: 'jpg', color: '#00C853', icon: 'mdi-file-jpg-box' },
        { type: 'png', color: '#2962FF', icon: 'mdi-file-png-box' }
      ],
      documentExtension: '',
      documentIcon: {},
      imageRoundedCorners: 0,
      imageSize: 'large',
      audioData: null,
      showEmojiPicker: false,
      emojiTranslation: {
        search: i18n.t('search'),
        categories: {
          Activity: i18n.t('emojiCategoryActivity'),
          Flags: i18n.t('emojiCategoryFlags'),
          Foods: i18n.t('emojiCategoryFoods'),
          Frequently: i18n.t('emojiCategoryFrequently'),
          Objects: i18n.t('emojiCategoryObjects'),
          Nature: i18n.t('emojiCategoryNature'),
          Peoples: i18n.t('emojiCategoryPeoples'),
          Symbols: i18n.t('emojiCategorySymbols'),
          Places: i18n.t('emojiCategoryPlaces')
        }
      },
      dialogVisibleCodeEmbed: false,
      recentColors: [],
      colorPickerValue: '#263238',
      loading: false,
      typeImge: '',
    }
  },
  computed: {
    colorPickerTextValue() {
      return '#ffffff'
      //return this.editor && this.editor.getAttributes('textStyle').color ? this.rgb2hex(this.editor.getAttributes('textStyle').color) : '#263238';
    },
    colorPickerButtonValue() {
      return '#ffffff'

      //return this.editor && this.editor.getAttributes('custom-link').backgroundColor ? this.rgb2hex(this.editor.getAttributes('custom-link').backgroundColor) : '#FFFFFF';
    },
     isGifUrl() {
      const urlWithoutParams = this.editorExtensionProperties.attrs.src.split('?')[0];
      return urlWithoutParams.endsWith('.gif');
      
    }
  },

  watch: {
    /*editor(){           
        if(this.editor.isActive('heading')){
            this.selectedTextSize = this.textSizes[this.editor.getAttributes('heading').level]
        }
        if(this.editor.isActive('paragraph')){
            this.selectedTextSize = this.textSizes[0]
        }
    }*/
    colorPickerTextValue(newColor) {
      this.setColorPickerColor(newColor);
    },
    colorPickerButtonValue(newColor) {
      this.setColorPickerColor(newColor);
    },
    colors(newVal){
      this.recentColors = newVal
    },
    editorTextSize(val) {
      this.selectedTextSize = this.textSizes[this.textSizes.findIndex(item => item.code === val)]
    },
    editorLinkProperties(val) {
      this.checkboxDesignAsButton = val.display == 'inline-block' ? true : false
      this.checkboxButtonRoundedCorners = val.roundedCorners != 0 ? true : false
      if (val.size != 0) {

        this.selectedButtonSize = this.buttonSizes[this.buttonSizes.findIndex(item => item.size == val.size)]
      }
    },
    editorActivateLinkOption() {
      if (this.dialogVisibleURL) {
        this.dialogVisibleURL = false
      }
    },
    editorExtensionProperties(val) {
      if (this.editorExtension == 'custom-image' || this.editorExtension == 'custom-image-banner') {
        this.imageRoundedCorners = 0
        if (val.attrs.style) {
          if (val.attrs.style.includes("border-radius")) {
            this.imageRoundedCorners = val.attrs.style.substring(
              val.attrs.style.lastIndexOf("border-radius:") + 14,
              val.attrs.style.lastIndexOf("vh")
            ).trim();
          }
        }       
        this.imageSize = val.attrs.size
      }
    }
  },
  created() {
    this.recentColors = this.colors
  },
  methods: {
    async setTextColor(value) {
      this.editor.chain().setColor(value).run()
      if(value){
        this.recentColors = this.recentColors.filter((v) => v !== value);
        this.recentColors.unshift(value)
        if (this.recentColors.length > 6) {
          this.recentColors.splice(6, this.recentColors.length - 6);
        }
        this.$emit('changeRecentColors', this.recentColors)
      }
    },
    setColorPickerColor(value) {
      this.colorPickerValue = value
    },
    setTextSize(value) {
      let align = 'left'
      if (this.editor.isActive({ textAlign: 'center' })) {
        align = 'center'
      }
      if (this.editor.isActive({ textAlign: 'right' })) {
        align = 'right'
      }
      if (this.editor.isActive({ textAlign: 'justify' })) {
        align = 'justify'
      }

      if (value.code == 'p-normal') {
        this.editor.chain().focus().setParagraph().setTextAlign(align).run()
      } else if (value.code == 'p-small') {
        this.editor.chain().focus().setParagraph({ class: 'small' }).setTextAlign(align).run()
      } else {
        this.editor.chain().focus().setHeading({ level: value.level }).setTextAlign(align).run()
      }
      this.$emit('changeSelectedTextSize', value.code)

    },
    rgb2hex(orig) {
      if (orig != undefined) {
        var a, isPercent,
          rgb = orig.replace(/\s/g, '').match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i),
          alpha = (rgb && rgb[4] || "").trim(),
          hex = rgb ? "#" +
            (rgb[1] | 1 << 8).toString(16).slice(1) +
            (rgb[2] | 1 << 8).toString(16).slice(1) +
            (rgb[3] | 1 << 8).toString(16).slice(1) : orig;
        if (alpha !== "") {
          isPercent = alpha.indexOf("%") > -1;
          a = parseFloat(alpha);
          if (!isPercent && a >= 0 && a <= 1) {
            a = Math.round(255 * a);
          } else if (isPercent && a >= 0 && a <= 100) {
            a = Math.round(255 * a / 100)
          } else {
            a = "";
          }
        }
        if (a) {
          hex += (a | 1 << 8).toString(16).slice(1);
        }
        return hex;
      }
    },
    showUploadImage() {
      this.$refs.file_input.$refs.input.click()
    },
    async uploadImage() {
      var alertMsg = ''
      this.typeImge = this.imageData.type
      if (this.imageData) {
          if (this.imageData.size > 5 * 1024 * 1024) {
                alertMsg = i18n.t('uploadCoverErrorWithGIF');
                this.$emit('proceedUploading', alertMsg, 'error');
              } else {
                if (this.imageData.type === 'image/gif') {
                   this.loading = true
                try {
                    let data = new FormData();
                    data.append('file', this.imageData);
                    data.append('type', 'content');
                    data.append('space_id', this.spaceId);
                    const response = await uploadImageInActivityPage(this.pageId, data) 
                    if(response.status == 200) {
                      this.uploadImageServer()
                        var url = response.data.meta.url
                        var path = response.data.meta.detail
                        // eslint-disable-next-line
                        this.editorExtensionProperties.attrs.src = url
                        this.editor.chain().focus().setImage({ src: url, path: path }).run()
                      
                        this.loading = false
                        
                    }
                } catch (e) {
                    if(e.response){
                        if (e.response.status == 422 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                this.alertValue = true
                                if(e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize'){
                                    alertMsg =  i18n.t('uploadCoverErrorWithGIF')
                                }else {
                                    alertMsg = i18n.t('serverError')
                                }
                            }
                        } else if (e.response.status == 403 ){
                            alertMsg =  i18n.t('forbidden')
                        } else {
                            alertMsg =  i18n.t('serverError')       
                        }
                        this.$emit('proceedUploading', alertMsg, 'error')
                    }else{
                        alertMsg =  i18n.t('serverError')
                        this.$emit('proceedUploading', alertMsg, 'error')
                    }
                    this.loading = false
                }
                } else {
                  this.imageToCropOnUpload = URL.createObjectURL(this.imageData);
                  this.imageTypeOnUpload = this.imageData.type;
                  this.cropOnUpload = true;
                  this.dialogVisibleCropper = true;
                }
              }
      }
      this.typeImge = false
      this.$refs.file_input.$refs.input.value = ''
    },
    async uploadImageServer() {
      var alertMsg = ''
      this.loadingUpload = true
      this.$emit('disableChangingEditor', true)
      try {
        if (this.page == 'activity_page') {
          let data = new FormData();
          data.append('file', this.imageData);
          data.append('type', 'content');
          data.append('space_id', this.spaceId);

          const response = await uploadImageInActivityPage(this.pageId, data)
          if (response.status == 200) {
            var url = response.data.meta.url
            var path = response.data.meta.detail
            // eslint-disable-next-line
            this.editorExtensionProperties.attrs.src = url
            this.editor.chain().focus().setImage({ src: url, path: path, style: this.editor.getAttributes(this.editorExtension).style, size: this.editor.getAttributes(this.editorExtension).size }).run()
            this.loadingUpload = false
          }
        } else if (this.page == 'checklist') {
          let data = new FormData();
          data.append('file', this.imageData);
          data.append('type', 'checkitem');
          data.append('space_id', this.spaceId);

          const response = await uploadImageInChecklist(this.checklistId, data)
          if (response.status == 200) {
            /*eslint-disable*/
            var url = response.data.meta.url
            var path = response.data.meta.detail

            this.editorExtensionProperties.attrs.src = url
            this.editor.chain().focus().setImage({ src: url, path: path, style: this.editor.getAttributes(this.editorExtension).style, size: this.editor.getAttributes(this.editorExtension).size }).run()
            this.loadingUpload = false
          }
        } else {
          let data = new FormData();
          var type = ''
          if (this.editor.options.editorProps.attributes.type == 'start' || this.editor.options.editorProps.attributes.type == 'end') {
            type = 'screen'
          } else {
            type = this.editor.options.editorProps.attributes.type
          }
          data.append('file', this.imageData);
          data.append('type', type);
          data.append('space_id', this.spaceId);

          const response = await uploadImageInSurvey(this.surveyId, data)
          if (response.status == 200) {
            /*eslint-disable*/
            var url = response.data.meta.url
            var path = response.data.meta.detail

            this.editorExtensionProperties.attrs.src = url
            this.editor.chain().focus().setImage({ src: url, path: path, style: this.editor.getAttributes(this.editorExtension).style, size: this.editor.getAttributes(this.editorExtension).size }).run()
            this.$nextTick(() => {
              this.$emit('changeSurveyImage', url, path, this.editor.options.editorProps.attributes.type)
            })
            this.loadingUpload = false

          }
        }
      } catch (e) {
        if (e.response) {
          if (e.response.status == 422) {
            for (let i = 0; i < e.response.data.errors.length; i++) {
              if (e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize') {
                alertMsg = i18n.t('uploadCoverError')
              } else {
                alertMsg = i18n.t('serverError')
              }
            }
          } else if (e.response.status == 403) {
            for (let i = 0; i < e.response.data.errors.length; i++) {
              if (e.response.data.errors[i].detail == 'Storage is full') {
                alertMsg = i18n.t('storageFullError')
              }
              else {
                this.$router.push({ name: 'PageAccessDenied' })
              }
            }
          } else {
            alertMsg = i18n.t('serverError')
          }
          this.$emit('proceedUploading', alertMsg, 'error')
        } else {
          alertMsg = i18n.t('serverError')
          this.$emit('proceedUploading', alertMsg, 'error')
        }
      }
      this.loadingUpload = false
      this.$emit('disableChangingEditor', false)
    },
    cropImage(data) {
      this.imageData = data
      this.uploadImageServer()
      this.dialogVisibleCropper = false
      this.imageToCropOnUpload = null
    },
    closeCropImage() {
      this.dialogVisibleCropper = false
      this.imageToCropOnUpload = null
    },
    showEmbedVideo() {
      this.dialogVisibleVideoEmbed = true
    },
    embedVideo(url) {
      this.loadingEmbed = true
      this.editorExtensionProperties.attrs.src = url
      this.editor.chain().focus().setIframe({ src: url, frameborder: '0', allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture', allowfullscreen: 'allowfullscreen' }).run()
      this.dialogVisibleVideoEmbed = false
      this.loadingEmbed = false
    },
    embedCode(code) {
      this.loadingEmbed = true
      this.editor.commands.setContent(code, true)
      this.dialogVisibleCodeEmbed = false
      this.editorExtensionProperties.attrs.src = this.editor.getAttributes('iframe').src
      this.loadingEmbed = false
    },
    async setButtonColor(value) {
      this.editor.chain().extendMarkRange('custom-link').setLink({ backgroundColor: value }).run()
      if(value){
        this.recentColors = this.recentColors.filter((v) => v !== value);
        this.recentColors.unshift(value)
        if (this.recentColors.length > 6) {
          this.recentColors.splice(6, this.recentColors.length - 6);
        }
        this.$emit('changeRecentColors', this.recentColors)
      }
    },
    setButtonSize(value) {
      this.editor.chain().extendMarkRange('custom-link').setLink({ size: value.size }).run()

    },
    setButtonRoundedCorners(value) {
      if (value) {
        this.editor.chain().extendMarkRange('custom-link').setLink({ roundedCorners: 24 }).run()
      } else {
        this.editor.chain().extendMarkRange('custom-link').setLink({ roundedCorners: 0 }).run()
      }
    },
    designAsButton(value) {
      if (this.editor.chain()) {
        if (value) {
          this.editor.chain().extendMarkRange('custom-link').updateAttributes('custom-link', { type: 'block', display: 'inline-block', roundedCorners: 0, backgroundColor: "#000000", size: 12 }).run()
        } else {
          //this.editor.chain().extendMarkRange('custom-link').resetAttributes('custom-link')
          this.editor.chain().extendMarkRange('custom-link').updateAttributes('custom-link', { type: 'link', display: '', roundedCorners: 0, backgroundColor: '', size: 0 }).run()
        }
      }
    },
    setLinkHref(url) {
      // empty
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('custom-link').unsetLink().run()
      } else {
        // update link
        this.editor.chain().focus().extendMarkRange('custom-link').setLink({ href: url }).run()
        this.checkboxDesignAsButton = this.editor.getAttributes('custom-link').display == 'inline-block' ? true : false
      }
      this.dialogVisibleURL = false
    },
    removeLink() {
      this.editor.chain().focus().extendMarkRange('custom-link').unsetLink().run()
      this.checkboxDesignAsButton = false
    },
    showUploadDocument() {
      this.$refs.document_input.$refs.input.click()
    },
    async uploadDocument() {
      var alertMsg = ''
      if (this.documentData) {
        if (this.documentData.size > 50 * 1024 * 1024) {
          alertMsg = i18n.t('uploadDocumentSizeError')
          this.$emit('proceedUploading', alertMsg, 'error')
        } else {
          this.loadingUpload = true
          this.$emit('disableChangingEditor', true)
          try {
            let data = new FormData();
            data.append('type', 'doc');
            data.append('file', this.documentData);
            data.append('space_id', this.spaceId);


            const response = await uploadDocumentInActivityPage(this.pageId, data)
            if (response.status == 200) {
              //this.documentExtension = response.data.meta.title.split('.').pop()
              this.documentExtension = response.data.meta.ext
              this.documentIcon = this.documentIcons[this.documentIcons.findIndex(item => item.type == this.documentExtension)]
              this.loadingUpload = false
              this.editor.commands.setContent(`
                                <p class="d-inline-block custom-file-doc-position">
                                    <a href="`+ response.data.meta.url + `" pathDoc="` + response.data.meta.detail + `"><span style="color: ` + this.documentIcon.color + `" class="mdi mdi-48px ` + this.documentIcon.icon + `">&nbsp;</span></A>
                                </p>
                                <p class="d-inline-block custom-file-doc-position custom-file-doc-title mt-4">
                                    <span style="color: #263238"><a href="`+ response.data.meta.url + `" pathDoc="` + response.data.meta.detail + `">` + response.data.meta.title + `</a></span><br />   
                                    <span style="color: #7C8386">`+ (response.data.meta.size / (1024 * 1024)).toFixed(3) + ` MB</span>            
                                </p>
                                 <p class="d-inline-block float-right mt-2">
                                    <a href="`+ response.data.meta.url + `" pathDoc="` + response.data.meta.detail + `"><span style="color: #7c8386" class="mdi mdi-36px mdi-download">&nbsp;</span></a>
                                 </p>
                                `,
                true
              )
            }
          } catch (e) {
            if (e.response) {
              if (e.response.status == 422) {
                for (let i = 0; i < e.response.data.errors.length; i++) {
                  if (e.response.data.errors[i].meta[0].code == 'extensionUnsupported') {
                    alertMsg = i18n.t('uploadDocumentExtensionError')
                  } else {
                    alertMsg = i18n.t('serverError')
                  }
                }
              } else if (e.response.status == 403) {
                for (let i = 0; i < e.response.data.errors.length; i++) {
                  if (e.response.data.errors[i].detail == 'Storage is full') {
                    alertMsg = i18n.t('storageFullError')
                  }
                  else {
                    this.$router.push({ name: 'PageAccessDenied' })
                  }
                }
              } else {
                alertMsg = i18n.t('serverError')
              }
              this.$emit('proceedUploading', alertMsg, 'error')
            } else {
              alertMsg = i18n.t('serverError')
              this.$emit('proceedUploading', alertMsg, 'error')
            }
          }
          this.loadingUpload = false
          this.$emit('disableChangingEditor', false)
        }
      }
      this.$refs.document_input.$refs.input.value = ''

    },
    addAccordion() {
      this.editor.schema.nodes.details.defaultAttrs.open = true
      this.editor.chain().focus().setDetails().run()
    },
    changeImageCorners() {
      this.editor.commands.updateAttributes(this.editorExtension, { style: 'border-radius:' + this.imageRoundedCorners + 'vh' })
    },
    changeImageSize(val) {
      this.editor.chain().focus().setAttributes({ size: val }).run()
    },
    preventLetters(e) {
      e = e || window.event;
      var charCode = (typeof e.which == "undefined") ? e.keyCode : e.which;
      var charStr = String.fromCharCode(charCode);

      if (!charStr.match(/^[0-9]+$/))
        e.preventDefault();

      if (e.target.value == 50)
        e.preventDefault();
    },
    showUploadAudio() {
      this.$refs.audio_input.$refs.input.click()
    },
    async uploadAudio() {
      var alertMsg = ''
      if (this.audioData) {
        if (this.audioData.size > 50 * 1024 * 1024) {
          alertMsg = i18n.t('uploadAudioSizeError')
          this.$emit('proceedUploading', alertMsg, 'error')
        } else {
          this.loadingUpload = true
          this.$emit('disableChangingEditor', true)
          try {
            let data = new FormData();
            data.append('type', 'audio');
            data.append('file', this.audioData);
            data.append('space_id', this.spaceId);

            const response = await uploadAudioInActivityPage(this.pageId, data)
            if (response.status == 200) {
              this.editorExtensionProperties.attrs.file = response.data.meta.url
              this.editorExtensionProperties.attrs.title = response.data.meta.title

              this.editor.chain().updateAttributes('audio', { file: response.data.meta.url, pathAudio: response.data.meta.detail, title: response.data.meta.title }).run()
            }
          } catch (e) {
            if (e.response) {
              if (e.response.status == 422) {
                for (let i = 0; i < e.response.data.errors.length; i++) {
                  if (e.response.data.errors[i].meta[0].code == 'extensionUnsupported') {
                    alertMsg = i18n.t('uploadAudioExtensionError')
                  } else {
                    alertMsg = i18n.t('serverError')
                  }
                }
              } else if (e.response.status == 403) {
                for (let i = 0; i < e.response.data.errors.length; i++) {
                  if (e.response.data.errors[i].detail == 'Storage is full') {
                    alertMsg = i18n.t('storageFullError')
                  }
                  else {
                    this.$router.push({ name: 'PageAccessDenied' })
                  }
                }
              } else {
                alertMsg = i18n.t('serverError')
              }
              this.$emit('proceedUploading', alertMsg, 'error')
            } else {
              alertMsg = i18n.t('serverError')
              this.$emit('proceedUploading', alertMsg, 'error')
            }
          }
          this.loadingUpload = false
          this.$emit('disableChangingEditor', false)
        }
      }
      this.$refs.audio_input.$refs.input.value = ''

    },
    selectEmoji(emoji) {
      this.editor.chain().focus().setEmoji(emoji['aliases'][0]).run()
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker
      this.$nextTick(() => {
        this.$refs.textActions.scrollIntoView()
      })
    },
    selectEmojiCategory(category) {
      var all = document.getElementsByClassName('category-title')
      for (let i = 0; i < all.length; i++) {
        if (all[i].innerHTML == i18n.t('emojiCategory' + category.name)) {
          all[i].scrollIntoView()
        }
      }
    },
  }
}
</script>